import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

// Shared premium easing (easeOutExpo-like — smooth, never bouncy).
export const EASE = [0.22, 1, 0.36, 1];

// Line-by-line masked reveal for hero-style headings.
export const MaskReveal = ({ lines, className = "", delay = 0, lineClass = "" }) => (
  <span className={className}>
    {lines.map((line, i) => (
      <span key={`${line}-${i}`} className="reveal-line">
        <motion.span
          className={`block ${lineClass}`}
          initial={{ y: "110%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 1, delay: delay + i * 0.1, ease: EASE }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </span>
);

// Scroll-triggered fade/rise for sections & media.
export const Reveal = ({ children, className = "", delay = 0, y = 40 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

// Staggered reveal container — children appear one after another.
export const StaggerReveal = ({ children, className = "", delay = 0, stagger = 0.12 }) => (
  <motion.div
    className={className}
    variants={{ hidden: {}, show: { transition: { staggerChildren: stagger, delayChildren: delay } } }}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-80px" }}
  >
    {children}
  </motion.div>
);

export const StaggerItem = ({ children, className = "", y = 50 }) => (
  <motion.div
    className={className}
    variants={{ hidden: { opacity: 0, y }, show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: EASE } } }}
  >
    {children}
  </motion.div>
);

// Parallax image — translates the image as it scrolls through the viewport.
export const ParallaxImage = ({ src, alt = "", className = "", imgClassName = "", range = 18 }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [`-${range}%`, `${range}%`]);
  if (reduce) {
    return (
      <div className={`overflow-hidden ${className}`}>
        <img src={src} alt={alt} className={`w-full h-full object-cover ${imgClassName}`} />
      </div>
    );
  }
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div style={{ y }} className="w-full h-full will-change-transform">
        <img src={src} alt={alt} className={`w-full h-full object-cover scale-[1.5] ${imgClassName}`} />
      </motion.div>
    </div>
  );
};

// Parallax wrapper for any content block (subtle vertical drift).
export const Parallax = ({ children, className = "", range = 40 }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [range, -range]);
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div ref={ref} style={{ y }} className={`will-change-transform ${className}`}>
      {children}
    </motion.div>
  );
};

// Clip-path mask reveal for large images — wipes open from the bottom while a
// gentle parallax drift plays underneath.
export const MaskImage = ({ src, alt = "", className = "", imgClassName = "", range = 14, parallax = true }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [`-${range}%`, `${range}%`]);
  const doParallax = parallax && !reduce;
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div
        initial={reduce ? { clipPath: "inset(0% 0 0 0)" } : { clipPath: "inset(100% 0 0 0)" }}
        whileInView={{ clipPath: "inset(0% 0 0 0)" }}
        viewport={{ once: true, margin: "-12%" }}
        transition={{ duration: 1.2, ease: EASE }}
        className="w-full h-full"
      >
        <motion.div style={doParallax ? { y } : undefined} className="w-full h-full will-change-transform">
          <img src={src} alt={alt} className={`w-full h-full object-cover ${doParallax ? "scale-[1.4]" : ""} ${imgClassName}`} />
        </motion.div>
      </motion.div>
    </div>
  );
};

// Dramatic scroll-scale — content enters slightly small, hits full scale at
// center, then shrinks hard and fades out as it scrolls away.
export const ScrollScale = ({ children, className = "", from = 0.85, exit = 0.4 }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 0.28, 0.6, 1], [from, 1, 1, exit]);
  const opacity = useTransform(scrollYProgress, [0, 0.22, 0.72, 0.95], [0.5, 1, 1, 0]);
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div ref={ref} style={{ scale, opacity }} className={`will-change-transform ${className}`}>
      {children}
    </motion.div>
  );
};
